import { useForm } from "react-hook-form";
import { ModalContext, toast, useScreenResolution } from "dash-ui";
import { Broker, useSendMessageToAgentMutation } from "gql/generated.graphql";
import { useContext, useState } from "react";
import BrokerAvailabilityModal from "containers/portfolio/AvailabilityModal/AvailabilityModal";
import AvatarPlaceholder4x3 from "../../../assets/images/png/avatar-placeholder-4x3.png";
import { FiGlobe } from "react-icons/fi";
import { MdPhone } from "react-icons/md";
import { formatByMask } from "dash-common";

type ContactFormData = {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
  propertyName?: string;
};

type IDXContactFormProps = {
  broker: Broker;
  propertyName: string;
  branding: {
    mainColor: string;
    textColor: string;
    backgroundColor: string;
  };
};

const IDXContactForm = (props: IDXContactFormProps) => {
  const avatar = props.broker.profile?.avatarFile?.publicUrl;
  const contactForm = useForm<ContactFormData>();
  const [sendMessageToAgent] = useSendMessageToAgentMutation();

  const { toggleModal, isOpenCurrent } = useContext(ModalContext);
  const [isMessageSent, setIsMessageSent] = useState(false);
  const {
    devices: { isMobile },
  } = useScreenResolution();

  async function onContactBroker(formData: ContactFormData) {
    if (!formData || !formData.name || !formData.email || !formData.message) {
      toast.error(
        "Please, fill all the required fields before sending the message."
      );
      return;
    }

    await sendMessageToAgent({
      variables: {
        input: {
          broker_email: props.broker.account.email,
          broker_name: props.broker.profile.firstName,
          customer_email: formData.email,
          customer_message: formData.message,
          customer_name: formData.name,
          customer_phone: formData.phone || "",
          portfolio_folder_name: "",
          property_name: props.propertyName,
        },
      },
    });
    contactForm.reset();
    setIsMessageSent(true);
  }

  const onClickAvailabilityButton = () => {
    if (props.broker) {
      toggleModal(true, "BrokerAvailabilityModal");
    }
  };

  if (!props.broker) {
    return <></>;
  }

  const renderMessageSentView = () => {
    return (
      <>
        <div className="flex flex-col h-full text-sm text-slate-800 justify-center">
          <div className="mb-4">
            <p className="text-center">
              Your message has been sent and the agent will contact you shortly.
            </p>
          </div>
          <div>
            <button
              className="flex items-center justify-center w-full py-2 mt-2 mb-8 sm:mb-0 font-semibold rounded text-sm transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-lg text-white"
              style={{
                backgroundColor: props.branding.mainColor,
              }}
              onClick={() => setIsMessageSent(false)}
            >
              Send New Message
            </button>
          </div>
        </div>
      </>
    );
  };

  const renderContactForm = () => {
    return (
      <form
        onSubmit={contactForm.handleSubmit(onContactBroker)}
        className="flex flex-col gap-2 h-full text-sm text-slate-800"
      >
        <label className="font-semibold">
          Your name*
          <input
            type="text"
            className="w-full rounded px-4 py-2 text-sm border text-black font-normal focus:outline-none"
            {...contactForm.register("name")}
            required
          />
        </label>
        <label className="font-semibold">
          Email*
          <input
            type="email"
            className="w-full rounded px-4 py-2 text-sm border  text-black font-normal focus:outline-none"
            {...contactForm.register("email")}
            required
          />
        </label>
        <label className="font-semibold">
          Phone
          <input
            type="text"
            className="w-full rounded px-4 py-2 text-sm border text-black font-normal focus:outline-none"
            {...contactForm.register("phone")}
          />
        </label>
        <label className="font-semibold flex flex-col grow">
          Message*
          <textarea
            className="w-full h-full px-4 py-2 rounded text-sm border text-black font-normal focus:outline-none"
            required
            {...contactForm.register("message")}
          />
        </label>
        <button
          type="submit"
          className="flex items-center justify-center w-full py-2 mt-2 mb-8 sm:mb-0 font-semibold rounded text-sm transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-lg text-white"
          style={{
            backgroundColor: props.branding.mainColor,
          }}
        >
          Send Message
        </button>
      </form>
    );
  };

  return (
    <>
      <div
        className="w-full rounded shadow-lg grid grid-cols-1 md:grid-cols-2 md:gap-12 relative p-8"
        style={{
          backgroundColor: "#FFFFFF",
          color: props.branding.textColor,
        }}
      >
        {/* Removed temporarily
        <div className="absolute top-2 right-4 sm:[absolute top-4 right-4]">
          <Icon size={50} maxHeight={20} name="exchangeLogo" />
        </div> 
        */}

        {/* Broker details */}
        <div className="flex flex-col">
          <img
            src={avatar || AvatarPlaceholder4x3}
            alt="Avatar image"
            className="rounded-xl aspect-[4/3] max-w-[300px] object-cover mb-4"
          />
          <div className="flex flex-col gap-4 text-slate-600">
            <div>
              <h2 className="text-2xl font-bold text-slate-800">
                {`${props.broker.profile.firstName} ${props.broker.profile.lastName}`}
              </h2>
              {props.broker.agencyName && (
                <h3 className="font-semibold">{props.broker.agencyName}</h3>
              )}
            </div>
            {props.broker.agencyWebsite && (
              <a
                href={
                  props.broker.agencyWebsite?.startsWith("https://")
                    ? props.broker.agencyWebsite || ""
                    : `https://${props.broker.agencyWebsite}`
                }
                target="_blank"
                className="flex gap-2 items-center cursor-pointer w-fit"
              >
                <FiGlobe className="text-lg" />
                <p>Visit website</p>
              </a>
            )}
            {props.broker.description && (
              <p className="text-sm">{props.broker.description}</p>
            )}
            <div className="flex flex-col gap-4 items-start text-slate-600">
              {props.broker.account.phone && (
                <>
                  <span className="text-md font-bold text-slate-500">
                    CONTACT ME
                  </span>
                  <a
                    href={`tel:${props.broker.account.phone?.dialCode}${props.broker.account.phone?.number}`}
                    className="text-white flex items-center justify-center gap-2 px-4 w-full h-10 rounded transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-lg"
                    style={{
                      backgroundColor: props.branding.mainColor,
                    }}
                  >
                    <MdPhone className="h-4 w-4" />
                    {props.broker.account.phone?.dialCode}{" "}
                    {formatByMask(
                      props.broker.account.phone?.number,
                      "(###) ###-####"
                    )}
                  </a>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="gap-4 flex flex-col rounded-md w-full h-full mt-4 md:mt-0">
          {isMessageSent ? renderMessageSentView() : renderContactForm()}
        </div>
      </div>
      {props.broker && <BrokerAvailabilityModal broker={props.broker} />}
    </>
  );
};

export default IDXContactForm;
